import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, Save, TestTube, Check, X, Eye, EyeOff,
  MessageCircle, Mail, Smartphone, Facebook, Instagram, Brain, Cloud,
  Loader2, AlertCircle, CheckCircle, Power, PowerOff
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const ICONS = {
  MessageCircle,
  Mail,
  Smartphone,
  Facebook,
  Instagram,
  Brain,
  Cloud
};

const IntegrationCard = ({ integration, onEdit, onToggle }) => {
  const Icon = ICONS[integration.icon] || Settings;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card className={`cursor-pointer transition-all bg-slate-800 border-slate-700 ${
        integration.is_enabled 
          ? 'border-green-500 bg-green-900/20' 
          : 'hover:border-slate-600'
      }`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                integration.is_enabled 
                  ? 'bg-green-900/50 text-green-400'
                  : integration.is_configured
                    ? 'bg-orange-900/50 text-orange-400'
                    : 'bg-slate-700 text-slate-400'
              }`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  {integration.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  {integration.is_enabled ? (
                    <Badge className="bg-green-900/50 text-green-400 border-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Ativo
                    </Badge>
                  ) : integration.is_configured ? (
                    <Badge variant="outline" className="text-orange-400 border-orange-700">
                      Configurado
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500 border-slate-600">
                      Não configurado
                    </Badge>
                  )}
                </div>
                {integration.last_updated && (
                  <p className="text-xs text-slate-500 mt-1">
                    Atualizado: {new Date(integration.last_updated).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={integration.is_enabled}
                onCheckedChange={() => onToggle(integration)}
                disabled={!integration.is_configured}
                data-testid={`toggle-${integration.type}`}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onEdit(integration)}
                data-testid={`edit-${integration.type}`}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Configurar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const IntegrationConfigModal = ({ integration, isOpen, onClose, onSave }) => {
  const { api } = useAuth();
  const [config, setConfig] = useState({});
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    if (integration && isOpen) {
      loadConfig();
    }
  }, [integration, isOpen]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/integrations/${integration.type}`);
      setConfig(response.data.config || {});
      setIsEnabled(response.data.is_enabled);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/admin/integrations/${integration.type}`, null, {
        params: { is_enabled: isEnabled },
        data: config
      });
      toast.success(`${integration.name} configurado com sucesso!`);
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      // Save first
      await api.put(`/admin/integrations/${integration.type}`, null, {
        params: { is_enabled: isEnabled },
        data: config
      });
      
      // Then test
      const response = await api.post(`/admin/integrations/${integration.type}/test`);
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Erro no teste' });
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!integration) return null;

  const Icon = ICONS[integration.icon] || Settings;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              integration.is_enabled 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>{integration.name}</DialogTitle>
              <DialogDescription>
                Configure as credenciais da integração
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {integration.fields?.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {field.type === 'select' ? (
                  <Select
                    value={config[field.key] || ''}
                    onValueChange={(value) => setConfig({ ...config, [field.key]: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Selecione ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options?.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === 'password' ? (
                  <div className="relative">
                    <Input
                      id={field.key}
                      type={showPasswords[field.key] ? 'text' : 'password'}
                      value={config[field.key] || ''}
                      onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                      placeholder={`Digite ${field.label.toLowerCase()}`}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(field.key)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords[field.key] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <Input
                    id={field.key}
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={config[field.key] || ''}
                    onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                    placeholder={`Digite ${field.label.toLowerCase()}`}
                  />
                )}
              </div>
            ))}

            {/* Enable toggle */}
            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Ativar integração</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Habilitar uso desta integração no sistema
                </p>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
                data-testid="toggle-enable"
              />
            </div>

            {/* Test result */}
            {testResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                testResult.success 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="text-sm">{testResult.message}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || loading}
                className="flex-1"
                data-testid="test-connection"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                Testar Conexão
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                data-testid="save-config"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminIntegrations() {
  const { api } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/integrations');
      setIntegrations(response.data.integrations);
    } catch (error) {
      toast.error('Erro ao carregar integrações');
      console.error('Error loading integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };

  const handleToggle = async (integration) => {
    if (!integration.is_configured) {
      toast.error('Configure a integração primeiro');
      return;
    }

    try {
      await api.post(`/admin/integrations/${integration.type}/toggle`);
      toast.success(
        integration.is_enabled 
          ? `${integration.name} desativado`
          : `${integration.name} ativado`
      );
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const categorizedIntegrations = {
    messaging: integrations.filter(i => ['whatsapp', 'sms', 'email'].includes(i.type)),
    social: integrations.filter(i => ['facebook', 'instagram'].includes(i.type)),
    ai: integrations.filter(i => ['gpt'].includes(i.type))
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">
          Integrações
        </h1>
        <p className="text-slate-400">
          Configure as integrações de APIs externas
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">
              {integrations.length}
            </div>
            <p className="text-sm text-slate-400">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {integrations.filter(i => i.is_enabled).length}
            </div>
            <p className="text-sm text-slate-400">Ativas</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-400">
              {integrations.filter(i => i.is_configured && !i.is_enabled).length}
            </div>
            <p className="text-sm text-slate-400">Configuradas</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-500">
              {integrations.filter(i => !i.is_configured).length}
            </div>
            <p className="text-sm text-slate-400">Pendentes</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700">Todas</TabsTrigger>
            <TabsTrigger value="messaging" className="data-[state=active]:bg-slate-700">Mensagens</TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-slate-700">Social</TabsTrigger>
            <TabsTrigger value="ai" className="data-[state=active]:bg-slate-700">IA</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {integrations.map((integration) => (
              <IntegrationCard
                key={integration.type}
                integration={integration}
                onEdit={handleEdit}
                onToggle={handleToggle}
              />
            ))}
          </TabsContent>

          <TabsContent value="messaging" className="space-y-4">
            {categorizedIntegrations.messaging.map((integration) => (
              <IntegrationCard
                key={integration.type}
                integration={integration}
                onEdit={handleEdit}
                onToggle={handleToggle}
              />
            ))}
          </TabsContent>

          <TabsContent value="social" className="space-y-4">
            {categorizedIntegrations.social.map((integration) => (
              <IntegrationCard
                key={integration.type}
                integration={integration}
                onEdit={handleEdit}
                onToggle={handleToggle}
              />
            ))}
          </TabsContent>

          <TabsContent value="ai" className="space-y-4">
            {categorizedIntegrations.ai.map((integration) => (
              <IntegrationCard
                key={integration.type}
                integration={integration}
                onEdit={handleEdit}
                onToggle={handleToggle}
              />
            ))}
          </TabsContent>
        </Tabs>
      )}

      {/* Config Modal */}
      <IntegrationConfigModal
        integration={selectedIntegration}
        isOpen={showConfigModal}
        onClose={() => {
          setShowConfigModal(false);
          setSelectedIntegration(null);
        }}
        onSave={loadIntegrations}
      />
    </div>
  );
}
